import { z } from 'zod';

/**
 * 共有データベース（docs/shared-database.md）。
 * 専用の VM に MariaDB と Redis を置き、許可したサーバーの VM からだけつなげる。
 * プラグイン（The New Economy など）がサーバーをまたいでデータをそろえるために使う。
 */
export const SHARED_DB_PORTS = { mariadb: 3306, redis: 6379 } as const;
/** agent の識別子（agent_versions・自動更新で使う。サーバー ID とはぶつからない） */
export const SHARED_DB_AGENT_ID = 'database';
/** MariaDB・Redis の既存の名前や管理用の名前とぶつかるもの */
export const SHARED_DB_RESERVED_NAMES = [
  'mysql',
  'information_schema',
  'performance_schema',
  'sys',
  'root',
  'default',
  'mcpanel',
  'test',
] as const;

/** データベース名・MariaDB のユーザー名・Redis のユーザー名を兼ねる */
export const SharedDbNameSchema = z
  .string()
  .regex(/^[a-z][a-z0-9_]{0,31}$/, '英小文字で始まる、英小文字・数字・_ の 1〜32 文字にしてください')
  .refine((name) => !(SHARED_DB_RESERVED_NAMES as readonly string[]).includes(name), 'この名前は使えません');

export const SharedDbHostInputSchema = z
  .object({
    node: z.string().min(1).max(64),
    cores: z.number().int().min(1).max(4).default(2),
    memoryMb: z.number().int().min(1024).max(8192).default(2048),
    dataDiskGb: z.number().int().min(10).max(200).default(20),
  })
  .strict();
export type SharedDbHostInput = z.infer<typeof SharedDbHostInputSchema>;

export const SharedDatabaseInputSchema = z
  .object({
    /** つないでよいサーバーの ID */
    servers: z.array(z.string().min(1).max(64)).max(20),
  })
  .strict()
  .refine((input) => new Set(input.servers).size === input.servers.length, '同じサーバーが重複しています');

export const SharedDbBackupSchema = z
  .object({ file: z.string().max(200), sizeBytes: z.number().int().min(0), createdAt: z.string().max(40) })
  .strict();
export const SharedDbStatusSchema = z
  .object({
    mariadb: z.object({ active: z.boolean(), version: z.string().max(200).optional() }).strict(),
    redis: z.object({ active: z.boolean(), version: z.string().max(200).optional() }).strict(),
    databases: z.array(z.object({ name: z.string().max(64), sizeBytes: z.number().int().min(0) }).strict()).max(200),
    backups: z.array(SharedDbBackupSchema).max(50),
    disk: z
      .object({ totalBytes: z.number().min(0), freeBytes: z.number().min(0) })
      .strict()
      .optional(),
    /** 最後に設定を反映した時刻と、その中身のハッシュ */
    appliedAt: z.string().max(40).optional(),
    appliedHash: z.string().max(80).optional(),
    /** 最後の反映・バックアップの失敗（あれば） */
    error: z.string().max(2000).optional(),
  })
  .strict();
export type SharedDbStatus = z.infer<typeof SharedDbStatusSchema>;

const Ipv4Schema = z.string().regex(/^(?:(?:25[0-5]|2[0-4]\d|1?\d?\d)\.){3}(?:25[0-5]|2[0-4]\d|1?\d?\d)$/);
const SecretSchema = z.string().regex(/^[A-Za-z0-9_-]{24,128}$/);
/** Panel → agent。全体を毎回送り、agent は差分を当てる（無いユーザーは消し、データは残す） */
export const SharedDbConfigSchema = z
  .object({
    databases: z
      .array(
        z.object({ name: SharedDbNameSchema, mariadbPassword: SecretSchema, redisPassword: SecretSchema }).strict(),
      )
      .max(50),
    allow: z.array(Ipv4Schema).max(100),
  })
  .strict();
export type SharedDbConfig = z.infer<typeof SharedDbConfigSchema>;

export const SharedDbPanelMessageSchema = z.discriminatedUnion('type', [
  z
    .object({
      id: z.string().uuid(),
      type: z.literal('apply_database'),
      hash: z.string().max(80),
      config: SharedDbConfigSchema,
    })
    .strict(),
  z.object({ id: z.string().uuid(), type: z.literal('database_status') }).strict(),
  z.object({ id: z.string().uuid(), type: z.literal('database_backup') }).strict(),
  z
    .object({
      id: z.string().uuid(),
      type: z.literal('update_agent'),
      version: z.string().max(50),
      sha256: z.string().regex(/^[a-f0-9]{64}$/),
    })
    .strict(),
]);
export type SharedDbPanelMessage = z.infer<typeof SharedDbPanelMessageSchema>;

export const SharedDbAgentMessageSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('hello'), role: z.literal('database'), version: z.string().max(50) }).strict(),
  z.object({ type: z.literal('status'), status: SharedDbStatusSchema }).strict(),
  z
    .object({
      type: z.literal('result'),
      id: z.string().uuid(),
      ok: z.boolean(),
      status: SharedDbStatusSchema.optional(),
      error: z.string().max(2000).optional(),
    })
    .strict(),
]);
export type SharedDbAgentMessage = z.infer<typeof SharedDbAgentMessageSchema>;

export type SharedDbHostState = 'provisioning' | 'ready' | 'error';
export interface SharedDbHostView {
  state: SharedDbHostState;
  node: string;
  vmid: number | null;
  address: string | null;
  cores: number;
  memoryMb: number;
  dataDiskGb: number;
  message: string | null;
  /** 作成の途中経過（日本語、最新が最後） */
  steps: string[];
  connected: boolean;
  agentVersion: string | null;
  status: SharedDbStatus | null;
  statusAt: string | null;
  createdAt: string;
}
export interface SharedDatabaseEntryView {
  name: string;
  servers: string[];
  /** 許可したサーバーのうち、今の IP が分からずつなげないもの */
  unreachable: string[];
  createdAt: string;
}
export interface SharedDatabaseView {
  host: SharedDbHostView | null;
  databases: SharedDatabaseEntryView[];
  /** 作成できるノード（オンラインのもの） */
  nodes: Array<{ name: string; role: string; memoryFreeMb: number }>;
  ports: typeof SHARED_DB_PORTS;
}
export interface SharedDbCredentials {
  name: string;
  host: string | null;
  mariadb: { port: number; database: string; user: string; password: string };
  redis: { port: number; user: string; password: string };
}
